/**
 * 詳細なエラーロギング機能
 * 
 * コンソールとローカルストレージへエラー情報を記録し、
 * 事後調査とデバッグを容易にする
 */

import { FetchError } from './fetchWithRetry';

const ERROR_LOG_KEY = 'yolo_biz_error_logs';
const MAX_LOGS = 50; // 最大保存件数

export interface ErrorLog {
  id: string;
  timestamp: string;
  errorCode: string;
  errorMessage: string;
  errorName: string;
  isRetryable: boolean;
  attempt?: number;
  userAgent: string;
  url: string;
  environment: string;
  additionalInfo?: any;
}

/**
 * ユニークIDを生成
 */
const generateLogId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 現在の環境情報を取得
 */
const getEnvironmentInfo = () => {
  return {
    userAgent: navigator.userAgent,
    url: window.location.href,
    environment: process.env.REACT_APP_ENVIRONMENT || 'unknown',
    timestamp: new Date().toISOString(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    online: navigator.onLine
  };
};

/**
 * エラーをローカルストレージに保存
 */
const saveToLocalStorage = (log: ErrorLog): void => {
  try {
    const existingLogs = getErrorLogsFromStorage();
    const newLogs = [log, ...existingLogs].slice(0, MAX_LOGS);
    localStorage.setItem(ERROR_LOG_KEY, JSON.stringify(newLogs));
  } catch (error) {
    console.error('Failed to save error log to localStorage:', error);
  }
};

/**
 * ローカルストレージからエラーログを取得
 */
const getErrorLogsFromStorage = (): ErrorLog[] => {
  try {
    const logs = localStorage.getItem(ERROR_LOG_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch (error) {
    console.error('Failed to retrieve error logs from localStorage:', error);
    return [];
  }
};

/**
 * コンソールに詳細なエラー情報を出力
 */
const logToConsole = (log: ErrorLog): void => {
  console.group(`🚨 Error Log [${log.errorCode}]`);
  console.error('Error Message:', log.errorMessage);
  console.error('Error Code:', log.errorCode);
  console.error('Error Name:', log.errorName);
  console.error('Timestamp:', log.timestamp);
  console.error('Is Retryable:', log.isRetryable);
  if (log.attempt) {
    console.error('Attempt:', log.attempt);
  }
  console.error('User Agent:', log.userAgent);
  console.error('URL:', log.url);
  console.error('Environment:', log.environment);
  if (log.additionalInfo) {
    console.error('Additional Info:', log.additionalInfo);
  }
  console.groupEnd();
};

/**
 * エラーをログに記録
 */
export const logError = (error: FetchError, context?: string): ErrorLog => {
  const envInfo = getEnvironmentInfo();
  
  const log: ErrorLog = {
    id: generateLogId(),
    timestamp: error.timestamp,
    errorCode: error.code,
    errorMessage: error.message,
    errorName: error.name,
    isRetryable: error.isRetryable,
    attempt: error.details?.attempt,
    userAgent: envInfo.userAgent,
    url: envInfo.url,
    environment: envInfo.environment,
    additionalInfo: {
      context,
      environmentDetails: envInfo,
      errorDetails: error.details
    }
  };

  // コンソールに出力
  logToConsole(log);

  // ローカルストレージに保存
  saveToLocalStorage(log);

  return log;
};

/**
 * 保存されたエラーログを取得
 */
export const getErrorLogs = (): ErrorLog[] => {
  return getErrorLogsFromStorage();
};

/**
 * エラーログをクリア
 */
export const clearErrorLogs = (): void => {
  try {
    localStorage.removeItem(ERROR_LOG_KEY);
    console.log('✅ Error logs cleared');
  } catch (error) {
    console.error('Failed to clear error logs:', error);
  }
};

/**
 * エラーログをエクスポート（サポート送信用）
 */
export const exportErrorLogs = (): string => {
  const logs = getErrorLogs();
  return JSON.stringify(logs, null, 2);
};

/**
 * 最新のエラーログを取得
 */
export const getLatestErrorLog = (): ErrorLog | null => {
  const logs = getErrorLogs();
  return logs.length > 0 ? logs[0] : null;
};

/**
 * エラーログの統計情報を取得
 */
export const getErrorStats = (): {
  total: number;
  retryable: number;
  nonRetryable: number;
  byErrorCode: Record<string, number>;
} => {
  const logs = getErrorLogs();
  
  const stats = {
    total: logs.length,
    retryable: logs.filter(log => log.isRetryable).length,
    nonRetryable: logs.filter(log => !log.isRetryable).length,
    byErrorCode: logs.reduce((acc, log) => {
      acc[log.errorCode] = (acc[log.errorCode] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  };

  return stats;
};

/**
 * デバッグモードでエラー統計を表示
 */
export const logErrorStats = (): void => {
  const stats = getErrorStats();
  console.group('📊 Error Statistics');
  console.log('Total Errors:', stats.total);
  console.log('Retryable Errors:', stats.retryable);
  console.log('Non-Retryable Errors:', stats.nonRetryable);
  console.log('By Error Code:', stats.byErrorCode);
  console.groupEnd();
};

